import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { toast } from "sonner";
import { AdminUsers } from "@/components/AdminUsers";
import { MaintenanceToggle } from "@/components/MaintenanceToggle";
import {
  Loader2, Plus, Trash2, Save, Shield, IndianRupee, Ticket, Unlock, Gift, BarChart3, Download,
} from "lucide-react";

const isExpired = (exp) => exp && new Date(exp + "T23:59:59") < new Date();

export default function Admin() {
  const { t, user, authLoading } = useApp();
  const navigate = useNavigate();
  const [coupons, setCoupons] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ code: "", percent: 20, expires_at: "", max_uses: "" });
  const [adding, setAdding] = useState(false);

  const load = async () => {
    try {
      const [c, s] = await Promise.all([api.get("/admin/coupons"), api.get("/admin/stats")]);
      setCoupons(c.data);
      setStats(s.data);
    } catch (e) {
      if (e?.response?.status === 401) navigate("/login");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate("/login"); return; }
    if (!user.is_admin) { setLoading(false); return; }
    load();
  }, [authLoading, user]); // eslint-disable-line

  if (authLoading || loading) {
    return <div className="min-h-[60vh] grid place-items-center text-amber-200"><Loader2 className="animate-spin" /></div>;
  }
  if (!user?.is_admin) {
    return (
      <div className="min-h-[60vh] grid place-items-center px-6 text-center">
        <div>
          <Shield className="mx-auto text-amber-300 mb-4" size={40} />
          <p className="text-slate-300">{t.admin.accessDenied}</p>
        </div>
      </div>
    );
  }

  const addCoupon = async () => {
    const code = form.code.trim().toUpperCase();
    if (!code || form.percent === "" || form.percent < 0 || form.percent > 100) {
      toast.error("Enter valid code and % (0-100)");
      return;
    }
    setAdding(true);
    try {
      await api.post("/admin/coupons", {
        code,
        percent: parseInt(form.percent, 10),
        active: true,
        expires_at: form.expires_at || null,
        max_uses: form.max_uses === "" ? null : parseInt(form.max_uses, 10),
      });
      toast.success(t.admin.created);
      setForm({ code: "", percent: 20, expires_at: "", max_uses: "" });
      load();
    } catch (e) {
      toast.error(e?.response?.status === 409 ? t.admin.exists : "Failed to add");
    } finally {
      setAdding(false);
    }
  };

  const patchLocal = (code, patch) =>
    setCoupons((cs) => cs.map((c) => (c.code === code ? { ...c, ...patch } : c)));

  const saveCoupon = async (c) => {
    try {
      await api.put(`/admin/coupons/${c.code}`, {
        percent: parseInt(c.percent, 10),
        expires_at: c.expires_at || null,
        clear_expiry: !c.expires_at,
        max_uses: c.max_uses === "" || c.max_uses === null ? null : parseInt(c.max_uses, 10),
        clear_max_uses: c.max_uses === "" || c.max_uses === null,
      });
      toast.success(t.admin.updated);
      load();
    } catch {
      toast.error("Failed to save");
    }
  };

  const toggleActive = async (c) => {
    try {
      await api.put(`/admin/coupons/${c.code}`, { active: !c.active });
      patchLocal(c.code, { active: !c.active });
      toast.success(t.admin.updated);
    } catch {
      toast.error("Failed");
    }
  };

  const deleteCoupon = async (code) => {
    if (!window.confirm(t.admin.confirmDelete)) return;
    try {
      await api.delete(`/admin/coupons/${code}`);
      setCoupons((cs) => cs.filter((c) => c.code !== code));
      toast.success(t.admin.deleted);
    } catch {
      toast.error("Failed");
    }
  };

  const downloadCsv = async (path, filename) => {
    try {
      const res = await api.get(path, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Export failed");
    }
  };

  const statCards = stats
    ? [
        { icon: IndianRupee, label: t.admin.revenue, value: `₹${stats.revenue_inr}`, color: "text-emerald-300" },
        { icon: Ticket, label: t.admin.payments, value: stats.paid_count, color: "text-amber-300" },
        { icon: Gift, label: t.admin.freeUnlocks, value: stats.free_count, color: "text-pink-300" },
        { icon: Unlock, label: t.admin.unlocked, value: `${stats.readings_unlocked}/${stats.readings_total}`, color: "text-sky-300" },
      ]
    : [];

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 py-8 sm:py-12" data-testid="admin-page">
      <div className="flex items-center gap-3 mb-2">
        <Shield className="text-amber-300 shrink-0" size={24} />
        <h1 className="font-serif text-2xl sm:text-4xl text-amber-100">{t.admin.title}</h1>
      </div>
      <p className="text-slate-400 text-sm sm:text-base mb-6 sm:mb-8">{t.admin.subtitle}</p>

      <MaintenanceToggle />

      {/* Sales dashboard */}
      <div className="flex items-center gap-2 mb-4">
        <BarChart3 className="text-amber-300" size={18} />
        <h2 className="text-lg text-amber-100 font-medium">{t.admin.statsTitle}</h2>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6 sm:mb-8" data-testid="admin-stats">
        {statCards.map((s) => (
          <div key={s.label} className="glass rounded-2xl p-4 sm:p-5 min-w-0" data-testid={`stat-${s.label}`}>
            <s.icon className={`${s.color} mb-2 sm:mb-3`} size={20} />
            <div className={`font-serif text-2xl sm:text-3xl ${s.color} truncate`}>{s.value}</div>
            <div className="text-xs text-slate-400 mt-1">{s.label}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 sm:flex sm:flex-wrap gap-3 mb-8 sm:mb-10">
        <button data-testid="export-payments-btn" onClick={() => downloadCsv("/admin/export/payments.csv", "astrofriend_payments.csv")}
          className="btn-ghost-gold rounded-full px-4 py-2.5 flex items-center justify-center gap-2 text-sm">
          <Download size={15} /> {t.admin.exportPayments}
        </button>
        <button data-testid="export-coupons-btn" onClick={() => downloadCsv("/admin/export/coupons.csv", "astrofriend_coupon_usage.csv")}
          className="btn-ghost-gold rounded-full px-4 py-2.5 flex items-center justify-center gap-2 text-sm">
          <Download size={15} /> {t.admin.exportCoupons}
        </button>
      </div>

      {/* Add coupon */}
      <div className="flex items-center gap-2 mb-4">
        <Ticket className="text-amber-300" size={18} />
        <h2 className="text-lg text-amber-100 font-medium">{t.admin.couponsTitle}</h2>
      </div>
      <div className="glass rounded-2xl p-4 sm:p-5 mb-6 grid grid-cols-2 sm:grid-cols-5 gap-3 items-end">
        <div className="col-span-2">
          <label className="text-xs text-slate-400 mb-1 block">{t.admin.code}</label>
          <input
            data-testid="admin-new-code"
            value={form.code}
            onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
            placeholder="ASTROFR..."
            className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2.5 text-slate-100 outline-none focus:border-amber-400/50 uppercase"
          />
        </div>
        <div>
          <label className="text-xs text-slate-400 mb-1 block">{t.admin.percent}</label>
          <input data-testid="admin-new-percent" type="number" min="0" max="100" value={form.percent}
            onChange={(e) => setForm({ ...form, percent: e.target.value })}
            className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2.5 text-slate-100 outline-none focus:border-amber-400/50" />
        </div>
        <div>
          <label className="text-xs text-slate-400 mb-1 block">{t.admin.maxUses}</label>
          <input data-testid="admin-new-maxuses" type="number" min="1" value={form.max_uses} placeholder="∞"
            onChange={(e) => setForm({ ...form, max_uses: e.target.value })}
            className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2.5 text-slate-100 outline-none focus:border-amber-400/50" />
        </div>
        <div className="col-span-2 sm:col-span-1">
          <label className="text-xs text-slate-400 mb-1 block">{t.admin.expiry}</label>
          <input data-testid="admin-new-expiry" type="date" value={form.expires_at}
            onChange={(e) => setForm({ ...form, expires_at: e.target.value })}
            className="w-full min-w-0 rounded-lg bg-white/5 border border-white/10 px-3 py-2.5 text-slate-100 outline-none focus:border-amber-400/50" />
        </div>
        <div className="col-span-2 sm:col-span-5">
          <button data-testid="admin-add-btn" onClick={addCoupon} disabled={adding}
            className="btn-gold rounded-full px-6 py-2.5 w-full sm:w-auto flex items-center justify-center gap-2 disabled:opacity-60">
            {adding ? <Loader2 className="animate-spin" size={16} /> : <Plus size={16} />} {t.admin.add}
          </button>
        </div>
      </div>

      {/* Coupon list */}
      {coupons.length === 0 ? (
        <p className="text-slate-500 text-sm">{t.admin.empty}</p>
      ) : (
        <div className="space-y-3">
          {coupons.map((c) => {
            const expired = isExpired(c.expires_at);
            const usage = stats?.coupon_usage?.find((u) => u.code === c.code) || {};
            const inputCls = "w-full min-w-0 rounded-lg bg-white/5 border border-white/10 px-2 py-2 text-slate-100 outline-none focus:border-amber-400/50 text-sm";
            return (
              <div key={c.code} data-testid={`coupon-row-${c.code}`}
                className="glass rounded-2xl p-4 flex flex-col lg:flex-row lg:items-center gap-3">
                <div className="lg:w-44 shrink-0 flex items-center justify-between lg:block gap-2">
                  <div className="font-mono text-amber-200 font-semibold text-sm sm:text-base break-all">{c.code}</div>
                  <span data-testid={`coupon-status-${c.code}`}
                    className={`text-xs px-2 py-0.5 rounded-full shrink-0 lg:inline-block lg:mt-1 ${c.active && !expired ? "bg-emerald-500/15 text-emerald-300" : "bg-slate-500/15 text-slate-400"}`}>
                    {expired ? t.admin.expired : c.active ? t.admin.active : t.admin.inactive}
                  </span>
                </div>

                <div className="flex-1 min-w-0">
                  <div className="grid grid-cols-[1fr_1fr_1.5fr] lg:flex lg:items-end gap-2 lg:gap-3">
                    <div className="lg:w-20">
                      <label className="text-[11px] text-slate-500 block mb-0.5">{t.admin.percent}</label>
                      <input data-testid={`coupon-percent-${c.code}`} type="number" min="0" max="100" value={c.percent}
                        onChange={(e) => patchLocal(c.code, { percent: e.target.value })} className={inputCls} />
                    </div>
                    <div className="lg:w-20">
                      <label className="text-[11px] text-slate-500 block mb-0.5">{t.admin.maxUses}</label>
                      <input data-testid={`coupon-maxuses-${c.code}`} type="number" min="0" placeholder="∞"
                        value={c.max_uses ?? ""}
                        onChange={(e) => patchLocal(c.code, { max_uses: e.target.value })} className={inputCls} />
                    </div>
                    <div className="lg:w-40">
                      <label className="text-[11px] text-slate-500 block mb-0.5">{t.admin.expiry}</label>
                      <input data-testid={`coupon-expiry-${c.code}`} type="date" value={c.expires_at || ""}
                        onChange={(e) => patchLocal(c.code, { expires_at: e.target.value })} className={inputCls} />
                    </div>
                  </div>
                  <div className="text-xs text-slate-400 mt-2 flex flex-wrap gap-x-3 gap-y-0.5">
                    <span>{t.admin.used}: <span className="text-slate-200">{c.used_count || 0}{c.max_uses ? `/${c.max_uses}` : ""}</span></span>
                    <span>{t.admin.redeemed}: <span className="text-slate-200">{usage.redeemed || 0}</span></span>
                    <span>{t.admin.discountGiven}: <span className="text-slate-200">₹{usage.discount_given || 0}</span></span>
                  </div>
                </div>

                <div className="grid grid-cols-3 lg:flex items-center gap-2 shrink-0">
                  <button data-testid={`coupon-save-${c.code}`} onClick={() => saveCoupon(c)}
                    className="btn-ghost-gold rounded-full px-3 py-2 flex items-center justify-center gap-1.5 text-sm">
                    <Save size={14} /> {t.admin.save}
                  </button>
                  <button data-testid={`coupon-toggle-${c.code}`} onClick={() => toggleActive(c)}
                    className="rounded-full px-3 py-2 text-sm bg-white/5 border border-white/10 text-slate-200 hover:bg-white/10 text-center">
                    {c.active ? t.admin.disable : t.admin.enable}
                  </button>
                  <button data-testid={`coupon-delete-${c.code}`} onClick={() => deleteCoupon(c.code)}
                    className="rounded-full px-3 py-2 text-sm bg-red-500/10 border border-red-500/20 text-red-300 hover:bg-red-500/20 flex items-center justify-center gap-1.5">
                    <Trash2 size={14} /> {t.admin.delete}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <AdminUsers />
    </div>
  );
}
